
export interface Project {
  title: string;
  description: string;
  image: string;
  categories: string[];
  slug: string;
  liveUrl?: string;
  longDescription?: string;
  features: string[];
  technologies: string[];
  knob:boolean // which means if the project is ready to be displayed on the websote or still incomplete
}

export const categories = [
  "All",
  "Chat",
  "Image",
  "Vision",
  "Audio",
  "Language",
  "Code",
  "Embeddings",
  "Rerank",
];

// Fetch projects from API
export async function getProjects(): Promise<Project[]> {
  try {
    // For server-side rendering, we can use absolute URL or directly call the database
    if (typeof window === 'undefined') {
      // Server-side: import prisma directly to avoid API call during build
      const prisma = (await import('@/prisma/lib/client')).default;
      
      const dbProjects = await prisma.Projects.findMany({
        where: {
          knob: true, // Only fetch projects that are ready to be displayed
        },
      });
      
      // Transform the database result to match our Project interface
      return dbProjects.map(project => ({
        title: project.title,
        description: project.description,
        image: project.image,
        categories: project.categories,
        slug: project.slug,
        liveUrl: project.liveUrl || undefined,
        longDescription: project.longDescription || undefined,
        features: project.features,
        technologies: project.technologies,
        knob: project.knob,
      }));
    } else {
      // Client-side: use API call
      const response = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000'}/api/projects`, {
        cache: 'force-cache', // Enable caching for SSG
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }
      
      const result = await response.json();
      return result.success ? result.data : [];
    }
  } catch (error) {
    console.error('Error fetching projects:', error);
    return [];
  }
}

// For backward compatibility and SSG, we'll export an empty array by default
export const projects: Project[] = [];

// export const projects: Project[] = [
//   {
//     title: "Llama-OCR",
//     description: "An OCR tool that takes documents (like receipts, PDFs with tables/charts) and outputs markdown.",
//     longDescription:
//       "Llama-OCR is a powerful document processing tool that converts complex documents into clean, structured markdown format. It excels at handling receipts, invoices, PDFs with tables, charts, and mixed content layouts.\n\nUnlike traditional OCR tools, Llama-OCR understands document structure and context, preserving formatting hierarchies and relationships between elements. This makes it ideal for digitizing business documents, academic papers, and financial records with high accuracy.",
//     image: "/images/project/llama-ocr.jpeg",
//     categories: ["Vision", "Language"],
//     slug: "llama-ocr",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Advanced table and chart recognition",
//       "Structured markdown output with proper formatting",
//       "Receipt and invoice processing",
//       "Multi-language document support",
//       "Batch document processing"
//     ],
//     technologies: ["Llama 3.1", "OCR APIs", "Python", "FastAPI"],
//     knob:true
//   },
//   {
//     title: "LogoCreator",
//     description: "A logo generator that creates professional logos in seconds using Flux Pro 1.1.",
//     longDescription:
//       "LogoCreator empowers businesses and entrepreneurs to generate professional-quality logos instantly. Using state-of-the-art AI image generation, it creates unique, brand-appropriate logos based on company descriptions and style preferences.\n\nThe tool understands design principles, color theory, and brand positioning to create logos that aren't just visually appealing but also strategically sound. It's perfect for startups, small businesses, and creative agencies needing rapid logo concepts and iterations.",
//     image: "/images/project/logo-creator.png",
//     categories: ["Image", "Vision"],
//     slug: "logocreator",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Instant professional logo generation",
//       "Multiple style and concept variations",
//       "Brand-appropriate color schemes",
//       "Vector-quality output formats",
//       "Industry-specific design understanding"
//     ],
//     technologies: ["Flux Pro 1.1", "React", "Together AI API"],
//     knob:true
//   },
//   {
//     title: "Agent Recipes",
//     description: "Explore common agent recipes with ready to copy code to improve your LLM applications.",
//     longDescription:
//       "Agent Recipes is a comprehensive collection of proven AI agent patterns and implementations. It provides developers with battle-tested code templates and architectural patterns for building sophisticated LLM applications.\n\nEach recipe includes detailed explanations, implementation guides, and ready-to-use code snippets. The platform covers everything from simple chatbots to complex multi-agent systems, making it an invaluable resource for developers at any level working with AI agents.",
//     image: "/images/project/agent-recipes.png",
//     categories: ["Code", "Language"],
//     slug: "agent-recipes",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Curated collection of agent patterns",
//       "Copy-paste ready code implementations",
//       "Detailed documentation and examples",
//       "Multi-agent system architectures",
//       "Integration guides for popular frameworks"
//     ],
//     technologies: ["Python", "LangChain", "Together AI API", "FastAPI"],
//     knob:true
//   },
//   {
//     title: "Code Arena",
//     description: "Watch AI models compete in real-time & vote on the best one.",
//     longDescription:
//       "Code Arena gamifies AI model evaluation by creating competitive coding challenges where different AI models solve problems in real-time. Users can watch the competition unfold and vote on which solutions they find most effective.\n\nThis platform provides valuable insights into model performance across different problem types and coding styles. It's educational for developers wanting to understand AI capabilities and entertaining for those interested in the competitive aspects of AI development.",
//     image: "/images/project/code-arena.jpg",
//     categories: ["Code", "Language"],
//     slug: "code-arena",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Real-time AI model competitions",
//       "Interactive voting and ranking system",
//       "Multiple programming languages support",
//       "Performance analytics and comparisons",
//       "Community-driven challenge creation"
//     ],
//     technologies: ["Next.js", "WebSockets", "Multiple LLM APIs", "PostgreSQL"],
//     knob:true
//   },
//   {
//     title: "LlamaCoder",
//     description: "An open source Claude Artifacts – generate small apps with one prompt. Powered by Llama 3 405B.",
//     longDescription:
//       "LlamaCoder is an innovative code generation platform that creates complete, functional applications from simple text prompts. Inspired by Claude Artifacts, it democratizes app development by making it accessible through natural language descriptions.\n\nThe platform generates not just code snippets but complete, working applications with proper structure, styling, and functionality. It's perfect for rapid prototyping, educational purposes, and helping non-programmers bring their ideas to life quickly.",
//     image: "/images/project/llama-coder.webp",
//     categories: ["Code", "Language"],
//     slug: "llamacoder",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Complete app generation from text prompts",
//       "Multiple framework support (React, Vue, etc.)",
//       "Instant preview and deployment",
//       "Code explanation and documentation",
//       "Open source and customizable"
//     ],
//     technologies: ["Llama 3 405B", "React", "Next.js", "Tailwind CSS"],
//     knob:true
//   },
//   {
//     title: "BlinkShot",
//     description: "A realtime AI image playground built with Flux Schnell on Together AI.",
//     longDescription:
//       "BlinkShot provides an ultra-fast, real-time image generation experience that responds to user inputs instantaneously. Built with Flux Schnell, it offers one of the fastest AI image generation experiences available.\n\nThe platform is perfect for creative experimentation, rapid ideation, and exploring the capabilities of modern AI image generation. Its real-time nature makes it feel more like a creative tool than a traditional AI service, enabling fluid creative workflows.",
//     image: "/images/project/blinkShot.webp",
//     categories: ["Image", "Vision"],
//     slug: "blinkshot",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Real-time image generation with minimal latency",
//       "Interactive creative playground interface",
//       "Multiple art styles and formats",
//       "Instant preview and iteration",
//       "High-quality output with Flux Schnell"
//     ],
//     technologies: ["Flux Schnell", "WebSockets", "React", "Together AI API"],
//     knob:true
//   },
//   {
//     title: "Product Description Generator",
//     description: "Upload a picture of any product and get descriptions for it in multiple languages.",
//     longDescription:
//       "This tool revolutionizes e-commerce content creation by generating compelling product descriptions from simple product images. It analyzes visual features, identifies key selling points, and creates marketing copy that converts.\n\nSupporting multiple languages, it's perfect for international e-commerce businesses, marketplace sellers, and catalog managers. The AI understands product categories, target demographics, and marketing psychology to create descriptions that drive sales.",
//     image: "/images/project/product-description-generator.webp",
//     categories: ["Vision", "Language"],
//     slug: "product-description-generator",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Image-to-text product analysis",
//       "Multi-language description generation",
//       "SEO-optimized content creation",
//       "Multiple tone and style options",
//       "Bulk product processing capabilities"
//     ],
//     technologies: ["Vision API", "Llama 3.1", "React", "i18n"],
//     knob:true
//   },
//   {
//     title: "TurboSeek",
//     description: "An AI search engine inspired by Perplexity that can give you real-time answers.",
//     longDescription:
//       "TurboSeek combines the power of web search with advanced AI reasoning to provide comprehensive, real-time answers to complex questions. Unlike traditional search engines, it synthesizes information from multiple sources and presents coherent, contextual responses.\n\nThe platform excels at research tasks, fact-checking, and providing up-to-date information on rapidly changing topics. It's perfect for researchers, journalists, students, and professionals who need reliable, current information quickly.",
//     image: "/images/project/turboseek.webp",
//     categories: ["Language", "Embeddings"],
//     slug: "turboseek",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Real-time web search integration",
//       "AI-powered answer synthesis",
//       "Source citation and verification",
//       "Follow-up question suggestions",
//       "Multi-format result presentation"
//     ],
//     technologies: ["Llama 3.1", "Search APIs", "RAG", "Next.js"],
//     knob:true
//   },
//   {
//     title: "Llama Tutor",
//     description: "A personal tutor that can explain any topic at any education level by using a search API along with Llama 3.1.",
//     longDescription:
//       "LlamaTutor is an adaptive educational AI that personalizes learning experiences for students at any level. It combines real-time information retrieval with advanced language understanding to provide clear, age-appropriate explanations on any topic.\n\nThe platform adapts its teaching style, complexity level, and examples based on the student's educational background and learning preferences. It's like having a patient, knowledgeable tutor available 24/7 for any subject.",
//     image: "/images/project/llama-tutor.webp",
//     categories: ["Language", "Chat"],
//     slug: "llamatutor",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Adaptive difficulty and explanation levels",
//       "Real-time information integration",
//       "Interactive Q&A sessions",
//       "Progress tracking and assessment",
//       "Multi-subject expertise"
//     ],
//     technologies: ["Llama 3.1", "Search APIs", "React", "Learning Analytics"],
//     knob:true
//   },
//   {
//     title: "PDFtoChat",
//     description: "Chat with your PDFs (blogs, textbooks, papers) with AI.",
//     longDescription:
//       "PDFtoChat transforms static documents into interactive conversations. Upload any PDF and engage in natural dialogue about its contents, ask questions, request summaries, or explore specific topics in depth.\n\nPerfect for researchers, students, and professionals who need to quickly extract insights from large documents. The AI understands document structure, references, and context to provide accurate, relevant responses based on the uploaded content.",
//     image: "/images/project/pic-chat-pdf.png",
//     categories: ["Chat", "Language"],
//     slug: "pdftochat",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Document upload and processing",
//       "Natural language querying",
//       "Context-aware responses",
//       "Citation and reference tracking",
//       "Summary and key insight extraction"
//     ],
//     technologies: ["PDF Processing", "RAG", "Llama 3.1", "Vector DB"],
//     knob:true
//   },
//   {
//     title: "PicMenu",
//     description: "A menu visualizer that takes a restaurant menu and generates nice images for each dish.",
//     longDescription:
//       "PicMenu revolutionizes restaurant marketing by automatically generating appetizing visuals for menu items. Using advanced AI image generation, it transforms text descriptions of dishes into professional-quality food photography.\n\nPerfect for restaurants, food delivery apps, and catering services, PicMenu eliminates the need for expensive food photography while maintaining consistent visual quality across all menu items. The tool understands culinary terminology and can generate culturally appropriate presentations for different cuisine types.",
//     image: "/images/project/picmenu.jpg",
//     categories: ["Image", "Vision"],
//     slug: "picmenu",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "AI-powered food image generation from text descriptions",
//       "Multiple cuisine style support",
//       "Professional food photography quality",
//       "Bulk menu processing capabilities",
//       "Customizable presentation styles"
//     ],
//     technologies: ["Flux Pro 1.1", "Next.js", "Together AI API"],
//     knob:true
//   },
//   {
//     title: "Napkins",
//     description: "A wireframe to app tool that can take in a UI mockup of a site and give you React code.",
//     longDescription:
//       "Napkins bridges the gap between design and development by converting UI mockups and wireframes directly into functional React code. Simply upload a sketch, wireframe, or design mockup, and get production-ready code.\n\nThis tool accelerates the development process by eliminating the manual translation from design to code. It understands common UI patterns, component structures, and modern React best practices to generate clean, maintainable code.",
//     image: "/images/project/napkins.webp",
//     categories: ["Code", "Vision"],
//     slug: "napkins",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Wireframe to React code conversion",
//       "Component-based architecture generation",
//       "Responsive design implementation",
//       "Modern CSS and styling",
//       "Clean, production-ready code output"
//     ],
//     technologies: ["Computer Vision", "Code Generation", "React", "Tailwind CSS"],
//     knob:true
//   },
//   {
//     title: "TwitterBio",
//     description: "An AI app that can generate your twitter/X bio for you.",
//     longDescription:
//       "TwitterBio helps users create compelling, authentic social media profiles that stand out. By analyzing personality traits, professional background, and interests, it generates multiple bio options that capture your unique voice.\n\nThe tool understands Twitter/X culture, character limits, and engagement best practices to create bios that not only describe you accurately but also attract your target audience and encourage connections.",
//     image: "/images/project/twitter-bio.png",
//     categories: ["Language", "Chat"],
//     slug: "twitterbio",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Personality-based bio generation",
//       "Multiple style and tone options",
//       "Character limit optimization",
//       "Engagement-focused writing",
//       "Professional and casual variations"
//     ],
//     technologies: ["Llama 3.1", "React", "Social Media Analytics"],
//     knob:true
//   },
//   {
//     title: "ExploreCareers",
//     description: "Upload your resume, add your interests, and get personalized career paths with AI.",
//     longDescription:
//       "ExploreCareers provides personalized career guidance by analyzing your existing skills, experience, and interests to suggest viable career paths. It goes beyond simple job matching to consider growth trajectories, skill gaps, and market trends.\n\nThe platform helps professionals make informed career decisions by providing detailed insights about potential roles, required skills, salary expectations, and steps needed to transition into new fields.",
//     image: "/images/project/explore-careers.jpg",
//     categories: ["Language", "Chat"],
//     slug: "explorecareers",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "Resume analysis and skill extraction",
//       "Personalized career path recommendations",
//       "Skill gap analysis and development plans",
//       "Market trend integration",
//       "Salary and growth projections"
//     ],
//     technologies: ["Document Processing", "Llama 3.1", "Career APIs", "React"],
//     knob:true
//   },
//   {
//     title: "NotesGPT",
//     description: "Record voice notes and get a transcript, summary, and action items with AI.",
//     longDescription:
//       "NotesGPT transforms voice recordings into organized, actionable content. Record meetings, lectures, brainstorming sessions, or personal thoughts, and get automatic transcription, intelligent summaries, and extracted action items.\n\nPerfect for busy professionals, students, and anyone who prefers verbal communication over typing. The AI understands context, identifies key points, and organizes information in a way that's immediately useful and actionable.",
//     image: "/images/project/notesgpt.webp",
//     categories: ["Audio", "Language"],
//     slug: "notesgpt",
//     liveUrl: "https://www.together.ai/demos",
//     features: [
//       "High-accuracy voice transcription",
//       "Intelligent content summarization",
//       "Automatic action item extraction",
//       "Meeting and lecture organization",
//       "Multi-language audio support"
//     ],
//     technologies: ["Speech-to-Text", "Llama 3.1", "Audio Processing", "React"],
//     knob:true
//   },
// ];

// export default projects;